import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { i18nLocale, timeAgo } from "../lib/locale";
import { toast } from "sonner";
import {
  Send, Clock, CheckCircle2, XCircle, PlayCircle, CheckCheck,
  Search, MapPin, Euro, Calendar, Bell, Sparkles, Store, Phone,
  ChevronDown, ChevronUp, Hourglass, AlertTriangle, Info, X,
  Navigation, CalendarDays, MessageCircle, ArrowUpDown,
} from "lucide-react";

const fmtDate = (iso) => {
  try {
    return new Date(`${iso}T00:00:00`).toLocaleDateString(i18nLocale(), { weekday: "short", day: "2-digit", month: "2-digit" });
  } catch { return iso; }
};

const fmtLong = (iso) => {
  try {
    return new Date(`${iso}T00:00:00`).toLocaleDateString(i18nLocale(), { day: "numeric", month: "short", year: "numeric" });
  } catch { return iso; }
};

const datesLabel = (need) => {
  const ds = [...(need.dates || [])].sort();
  if (ds.length) return ds.length > 1 ? `${fmtLong(ds[0])} → ${fmtLong(ds[ds.length - 1])}` : fmtLong(ds[0]);
  if (need.start_date) return `${fmtLong(need.start_date)}${need.end_date && need.end_date !== need.start_date ? ` → ${fmtLong(need.end_date)}` : ""}`;
  return "—";
};

const isOngoing = (p) => {
  if (p.status !== "confirmed") return false;
  const need = p.need || {};
  const ds = [...(need.dates || [])].sort();
  const start = ds[0] || need.start_date;
  const end = ds[ds.length - 1] || need.end_date || start;
  const today = new Date().toISOString().slice(0, 10);
  return !!start && start <= today && today <= (end || start);
};

export default function MissionsFeed() {
  const { t } = useTranslation();
  const [proposals, setProposals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [query, setQuery] = useState("");
  const [sort, setSort] = useState("date");
  const [tipDismissed, setTipDismissed] = useState(() => localStorage.getItem("rp_missions_tip") === "1");

  const load = async () => {
    try {
      const { data } = await api.get("/candidate/proposals");
      setProposals(Array.isArray(data) ? data : []);
      (Array.isArray(data) ? data : [])
        .filter((p) => p.status === "notified")
        .forEach((p) => api.post(`/candidate/proposals/${p.id}/seen`).catch(() => {}));
    } catch { setProposals([]); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const act = async (p, action) => {
    if (action === "refuse" && !window.confirm(t("proposals.confirm_refuse"))) return;
    setBusy(p.id);
    try {
      await api.post(`/candidate/proposals/${p.id}/action`, { action });
      toast.success(action === "accept" ? t("proposals.accept_success") : t("proposals.refuse_success"));
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    setBusy(null);
  };

  const cancelAcceptance = async (p) => {
    if (!window.confirm(t("proposals.confirm_cancel"))) return;
    setBusy(p.id);
    try {
      await api.post(`/candidate/proposals/${p.id}/cancel`);
      toast.success(t("proposals.cancel_success"));
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    setBusy(null);
  };

  const list = useMemo(() => {
    let l = proposals;
    const q = query.trim().toLowerCase();
    if (q) {
      l = l.filter((p) =>
        (p.pharmacy_name || "").toLowerCase().includes(q) ||
        (p.need?.title || "").toLowerCase().includes(q) ||
        (p.need?.role_type || "").toLowerCase().includes(q) ||
        (p.need?.address || "").toLowerCase().includes(q)
      );
    }
    l = [...l];
    if (sort === "score") l.sort((a, b) => (b.score || 0) - (a.score || 0));
    else l.sort((a, b) => {
      const da = (a.need?.dates || [a.need?.start_date || ""]).slice().sort()[0] || "";
      const db_ = (b.need?.dates || [b.need?.start_date || ""]).slice().sort()[0] || "";
      return da.localeCompare(db_);
    });
    return l;
  }, [proposals, query, sort]);

  return (
    <div data-testid="candidate-proposals-page">
      <div className="mb-6">
        <h1 className="text-3xl sm:text-4xl font-medium tracking-tight" style={{ fontFamily: "Outfit" }}>{t("proposals.title")}</h1>
        <p className="text-sm text-[#6B726A] mt-1 flex items-center gap-1.5">
          <Sparkles className="w-3.5 h-3.5 text-[#1A4D2E]" /> {t("proposals.subtitle")}
        </p>
      </div>

      {/* Recherche + tri */}
      <div className="flex flex-col sm:flex-row gap-3 mb-5">
        <div className="relative flex-1">
          <Search className="w-4 h-4 text-[#9AA097] absolute left-3.5 top-1/2 -translate-y-1/2" />
          <input value={query} onChange={(e) => setQuery(e.target.value)} data-testid="proposals-search"
            placeholder={t("proposals.search_placeholder")}
            className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-[#E2E5E0] bg-white text-sm focus:outline-none focus:border-[#1A4D2E]/50" />
        </div>
        <button onClick={() => setSort((s) => (s === "date" ? "score" : "date"))} data-testid="proposals-sort"
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl border border-[#E2E5E0] bg-white text-sm text-[#6B726A] hover:border-[#1A4D2E]/40">
          <ArrowUpDown className="w-4 h-4" />
          {sort === "date" ? t("proposals.sort_date") : t("proposals.sort_score")}
        </button>
      </div>

      {loading ? (
        <div className="text-sm text-[#6B726A] p-8 text-center">…</div>
      ) : list.length === 0 ? (
        <div className="rp-card p-10 text-center text-sm text-[#6B726A]" data-testid="proposals-empty">{t("proposals.empty_generic")}</div>
      ) : (
        <div className="space-y-4">
          {list.map((p) => (
            <MissionCard key={p.id} p={p} t={t} busy={busy === p.id}
              onAction={(a) => act(p, a)} onCancel={() => cancelAcceptance(p)} />
          ))}
        </div>
      )}

      {!tipDismissed && (
        <div className="mt-6 flex items-start gap-3 p-4 rounded-2xl bg-[#EEF3FB] border border-[#D6E2F5]" data-testid="missions-tip">
          <div className="w-8 h-8 rounded-full bg-[#3B5BDB] grid place-items-center shrink-0">
            <Info className="w-4 h-4 text-white" />
          </div>
          <div className="flex-1 text-sm">
            <div className="font-medium text-[#1E3A8A]">{t("proposals.good_to_know")}</div>
            <div className="text-[#3B5B8C] text-xs mt-0.5">{t("proposals.good_to_know_desc")}</div>
          </div>
          <button onClick={() => { setTipDismissed(true); localStorage.setItem("rp_missions_tip", "1"); }}
            data-testid="missions-tip-close" className="text-[#6B726A] hover:text-[#1f2421]">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}
    </div>
  );
}

const STATUS_CHIP = {
  notified:  { key: "proposals.status_chip_proposed",  dot: "#3B5BDB", bg: "#E8EDF7", fg: "#3B5BDB" },
  seen:      { key: "proposals.status_chip_proposed",  dot: "#3B5BDB", bg: "#E8EDF7", fg: "#3B5BDB" },
  accepted:  { key: "proposals.accepted_by_you",       dot: "#C97A20", bg: "#FBEFDA", fg: "#C97A20" },
  confirmed: { key: "proposals.confirmed_by_pharmacy", dot: "#1A9E55", bg: "#E8F0EA", fg: "#1A4D2E" },
  completed: { key: "proposals.status_completed",      dot: "#3F2EB2", bg: "#E8E5FF", fg: "#3F2EB2" },
  refused:   { key: "proposals.status_refused",        dot: "#E3655B", bg: "#FDE4E2", fg: "#A53A2F" },
  declined:  { key: "proposals.declined_by_pharmacy",  dot: "#E3655B", bg: "#FDE4E2", fg: "#A53A2F" },
  expired:   { key: "proposals.status_expired",        dot: "#6B726A", bg: "#F0F2EF", fg: "#6B726A" },
  cancelled: { key: "proposals.status_cancelled",      dot: "#E3655B", bg: "#FDE4E2", fg: "#A53A2F" },
};

function MissionCard({ p, t, busy, onAction, onCancel }) {
  const nav = useNavigate();
  const [openDetails, setOpenDetails] = useState(false);
  const need = p.need || {};
  const chip = STATUS_CHIP[p.status] || STATUS_CHIP.seen;
  const actionable = ["notified", "seen"].includes(p.status);
  const dates = [...(need.dates || [])].sort();
  const compatDates = (p.compatible_dates && p.compatible_dates.length ? [...p.compatible_dates].sort() : dates);
  const shownDates = compatDates.slice(0, 3);
  const slots = need.global_slots || [];
  const mapsUrl = need.address ? `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(need.address)}` : null;

  return (
    <div className="bg-white rounded-2xl border border-[#E2E5E0] p-5" data-testid={`proposal-card-${p.id}`}>
      <div className="grid lg:grid-cols-[minmax(0,1.1fr)_minmax(0,1.4fr)_minmax(0,1fr)] gap-5">
        {/* Colonne 1 : pharmacie */}
        <div className="min-w-0">
          <div className="flex items-start gap-3">
            <div className="w-11 h-11 rounded-full grid place-items-center shrink-0" style={{ background: `${chip.dot}1A` }}>
              <Store className="w-5 h-5" style={{ color: chip.dot }} />
            </div>
            <div className="min-w-0">
              <div className="text-base font-medium truncate" style={{ fontFamily: "Outfit" }}>
                {p.pharmacy_name || need.title || "—"}
              </div>
              <div className="text-sm text-[#6B726A]">{need.role_type || "—"}</div>
            </div>
          </div>
          {need.address && (
            <div className="text-xs text-[#6B726A] mt-3 flex items-start gap-1.5">
              <MapPin className="w-3.5 h-3.5 shrink-0 mt-0.5" /> <span>{need.address}</span>
            </div>
          )}
          {typeof p.distance_km === "number" && (
            <div className="text-xs text-[#9AA097] mt-1 ml-5">{t("proposals.km_away", { km: p.distance_km })}</div>
          )}
          {need.urgent && (
            <div className="mt-2 inline-flex items-center gap-1 text-xs text-[#E3655B]">
              <AlertTriangle className="w-3.5 h-3.5" /> {t("needs_page.urgent")}
            </div>
          )}
          {typeof p.score === "number" && (
            <div className="mt-3 inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full bg-[#E8F0EA] text-[#1A4D2E]"
              data-testid={`proposal-score-${p.id}`}>
              <Sparkles className="w-3 h-3" /> {t("proposals.match_global", { pct: p.score })}
            </div>
          )}
        </div>

        {/* Colonne 2 : plages horaires compatibles */}
        <div className="min-w-0 lg:border-l lg:border-[#E2E5E0]/70 lg:pl-5">
          <div className="text-sm font-medium">{t("proposals.compatible_slots")}</div>
          <div className="text-xs text-[#6B726A] mt-0.5">{t("proposals.compatible_slots_desc")}</div>
          <div className="mt-3 space-y-2">
            {shownDates.length > 0 ? shownDates.map((d) => (
              <div key={d} className="flex items-center gap-2 flex-wrap">
                <span className="inline-flex items-center gap-1.5 text-xs font-medium w-24 shrink-0">
                  <Bell className="w-3 h-3 text-[#6B726A]" /> {fmtDate(d)}
                </span>
                {(slots.length ? slots : [{ start: "09:00", end: "19:00" }]).map((s, i) => (
                  <span key={i} className="text-[11px] px-2 py-1 rounded-lg font-medium"
                    style={{ background: `${chip.dot}12`, color: chip.dot }}>
                    {s.start} - {s.end}
                  </span>
                ))}
              </div>
            )) : (
              <div className="flex items-center gap-2 flex-wrap">
                <span className="inline-flex items-center gap-1.5 text-xs font-medium">
                  <Calendar className="w-3 h-3 text-[#6B726A]" /> {datesLabel(need)}
                </span>
                {slots.map((s, i) => (
                  <span key={i} className="text-[11px] px-2 py-1 rounded-lg font-medium"
                    style={{ background: `${chip.dot}12`, color: chip.dot }}>{s.start} - {s.end}</span>
                ))}
              </div>
            )}
            {compatDates.length > 3 && (
              <div className="text-[11px] text-[#9AA097]">{t("proposals.more_dates", { count: compatDates.length - 3 })}</div>
            )}
          </div>
          <div className="mt-4 pt-3 border-t border-[#E2E5E0]/60 text-xs text-[#6B726A] flex items-center gap-4 flex-wrap">
            <span className="inline-flex items-center gap-1"><Calendar className="w-3.5 h-3.5" /> {datesLabel(need)}</span>
            {need.hourly_rate ? (
              <span className="inline-flex items-center gap-1"><Euro className="w-3.5 h-3.5" /> {t("proposals.rem_label", { rate: need.hourly_rate })}</span>
            ) : null}
          </div>
        </div>

        {/* Colonne 3 : statut + actions */}
        <div className="min-w-0 lg:border-l lg:border-[#E2E5E0]/70 lg:pl-5">
          <span className="inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full"
            style={{ background: chip.bg, color: chip.fg }} data-testid={`proposal-status-${p.id}`}>
            <span className="w-1.5 h-1.5 rounded-full" style={{ background: chip.dot }} />
            {t(chip.key)}
          </span>

          {actionable && (
            <>
              {p.notified_at && (
                <div className="text-xs text-[#6B726A] mt-3">{t("proposals.notified_ago", { ago: timeAgo(p.notified_at) })}</div>
              )}
              {typeof p.score === "number" && (
                <div className="mt-3 p-3 rounded-xl bg-[#F3F6FB] border border-[#E2E9F5] text-sm" data-testid={`proposal-matchbox-${p.id}`}>
                  {t("proposals.match_box_prefix")} <span className="font-semibold text-[#1E3A8A]">{p.score}%</span> {t("proposals.match_box_suffix")}
                </div>
              )}
              <button onClick={() => setOpenDetails((v) => !v)} data-testid={`proposal-details-${p.id}`}
                className="mt-3 text-xs text-[#1A4D2E] font-medium inline-flex items-center gap-1 hover:underline">
                {t("proposals.view_need_detail")}
                {openDetails ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
              </button>
              <div className="flex gap-2 mt-4">
                <button onClick={() => onAction("refuse")} disabled={busy} data-testid={`proposal-refuse-${p.id}`}
                  className="flex-1 px-4 py-2.5 rounded-xl text-sm font-medium border border-[#E3655B]/40 text-[#E3655B] hover:bg-[#FFF4F3] disabled:opacity-50">
                  {t("proposals.refuse")}
                </button>
                <button onClick={() => onAction("accept")} disabled={busy} data-testid={`proposal-accept-${p.id}`}
                  className="flex-1 px-4 py-2.5 rounded-xl text-sm font-medium bg-[#1A4D2E] text-white hover:bg-[#143C24] disabled:opacity-50">
                  {t("proposals.accept")}
                </button>
              </div>
            </>
          )}

          {p.status === "accepted" && (
            <>
              <div className="text-sm text-[#6B726A] mt-3 flex items-start gap-1.5">
                <Hourglass className="w-4 h-4 shrink-0 mt-0.5" /> {t("proposals.waiting_pharmacy")}
              </div>
              <button onClick={onCancel} disabled={busy} data-testid={`proposal-cancel-${p.id}`}
                className="mt-4 w-full px-4 py-2.5 rounded-xl text-sm font-medium border border-[#E3655B]/40 text-[#E3655B] hover:bg-[#FFF4F3] disabled:opacity-50">
                {t("proposals.cancel_acceptance")}
              </button>
            </>
          )}

          {["confirmed", "completed"].includes(p.status) && (
            <>
              <div className="text-sm text-[#6B726A] mt-3">
                {p.status === "confirmed" ? t("proposals.mission_confirmed") : t("proposals.status_completed")}
              </div>
              {p.pharmacy_phone && (
                <div className="text-xs text-[#6B726A] mt-2 inline-flex items-center gap-1.5">
                  <Phone className="w-3.5 h-3.5" /> {p.pharmacy_phone}
                </div>
              )}
              <div className="flex gap-2 mt-4 flex-wrap">
                {mapsUrl && (
                  <a href={mapsUrl} target="_blank" rel="noreferrer" data-testid={`proposal-itinerary-${p.id}`}
                    className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-medium border border-[#1A9E55]/40 text-[#1A9E55] hover:bg-[#E8F0EA]">
                    <Navigation className="w-3.5 h-3.5" /> {t("proposals.itinerary")}
                  </a>
                )}
                <button onClick={() => nav("/availabilities")} data-testid={`proposal-calendar-${p.id}`}
                  className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-medium border border-[#1A9E55]/40 text-[#1A9E55] hover:bg-[#E8F0EA]">
                  <CalendarDays className="w-3.5 h-3.5" /> {t("proposals.calendar_btn")}
                </button>
                <button onClick={() => nav("/messages")} data-testid={`proposal-contact-${p.id}`}
                  className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-medium border border-[#1A9E55]/40 text-[#1A9E55] hover:bg-[#E8F0EA]">
                  <MessageCircle className="w-3.5 h-3.5" /> {t("proposals.contact")}
                </button>
              </div>
            </>
          )}

          {["refused", "expired", "cancelled", "declined"].includes(p.status) && p.responded_at && (
            <div className="text-xs text-[#6B726A] mt-3">
              {t("proposals.refused_on", { date: fmtLong(p.responded_at.slice(0, 10)) })}
            </div>
          )}
        </div>
      </div>

      {openDetails && (
        <div className="mt-4 pt-4 border-t border-[#E2E5E0]/70 grid sm:grid-cols-2 gap-3 text-sm" data-testid={`proposal-detail-body-${p.id}`}>
          {(p.criteria || []).length > 0 && (
            <div className="p-3 rounded-xl bg-[#F7F8F6]" data-testid={`proposal-criteria-${p.id}`}>
              <div className="text-[11px] uppercase tracking-wider font-semibold text-[#6B726A] mb-1.5 flex items-center gap-1">
                <Sparkles className="w-3 h-3 text-[#1A4D2E]" /> {t("proposals.why_you")}
              </div>
              <div className="flex flex-wrap gap-1.5">
                {p.criteria.map((c, i) => (
                  <span key={i} className="text-[11px] px-2 py-0.5 rounded-full bg-[#E8F0EA] text-[#1A4D2E]">{c}</span>
                ))}
              </div>
            </div>
          )}
          <div className="p-3 rounded-xl border border-[#E2E5E0]">
            <div className="text-[11px] uppercase tracking-wider font-semibold text-[#6B726A] mb-1.5 flex items-center gap-1">
              <Calendar className="w-3 h-3" /> {t("proposals.dates")}
            </div>
            {dates.map((d) => <div key={d} className="text-xs py-0.5">{fmtLong(d)}</div>)}
            {!dates.length && <div className="text-xs">{datesLabel(need)}</div>}
            {(need.skills || []).length > 0 && (
              <div className="flex flex-wrap gap-1 mt-2">
                {need.skills.map((s) => <span key={s} className="text-[10px] px-2 py-0.5 rounded-full border border-[#E2E5E0] text-[#6B726A]">{s}</span>)}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
